<template>
  <div>
    <SummaryTitle title="Project"/>
    <SummaryItem itemTitle="Project Name">
      <template v-slot:content>
        {{ projectName }}
      </template>
    </SummaryItem>
    <SummaryItem itemTitle="Anticipated Revenue">
      <template v-slot:content>
        <div class="d-flex align-center">
          <span>{{ projectAnticipatedRevenue ? currency(projectAnticipatedRevenue) : 'N/A' }}</span>
          <v-tooltip left v-if="anticipatedRevenueHint">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                color="warning"
                class="ml-2"
              >
                warning
              </v-icon>
            </template>
            <span style="white-space: pre-line; display: inline-block; max-width: 250px;">
              {{ anticipatedRevenueHint }}
            </span>
          </v-tooltip>
        </div>
      </template>
    </SummaryItem>

    <!-- Candidates Header -->
    <div class="candidates-header">
      <SummaryTitle title="Candidates"/>

      <div class="toggle">
        <button
          :class="['toggle-btn', { active: selectedView === 'contract' }]"
          @click="selectedView = 'contract'"
        >
          Contract Details
        </button>

        <button
          :class="['toggle-btn', { active: selectedView === 'commissions' }]"
          @click="selectedView = 'commissions'"
        >
          Commissions
        </button>
      </div>
    </div>

    <!-- Candidates List -->
    <div
      v-for="candidate in candidates"
      :key="candidate.id"
      class="card mb-3"
    >
      <h4>
        {{
          !!candidate.firstName && !!candidate.lastName
            ? candidate.firstName + ' ' + candidate.lastName
            : 'Missing Candidate Name'
        }}
      </h4>

      <div v-if="selectedView == 'contract'">
        <div class="item">
          <span class="label">Job Title</span>
          <span class="dots"></span>
          <span class="value">
            {{ !!candidate.dealAssignment.jobTitle ? candidate.dealAssignment.jobTitle : 'Missing Job Title' }}
          </span>
        </div>

        <div class="item">
          <span class="label">Start Date</span>
          <span class="dots"></span>
          <span class="value">
            {{ !!candidate.dealAssignment.startAt ? formatDate(candidate.dealAssignment.startAt) : 'Missing Start Date' }}
          </span>
        </div>

        <div class="item">
          <span class="label">Pay Rate</span>
          <span class="dots"></span>
          <span class="value">
            {{ !!candidate.dealAssignment.payRate && !!candidate.dealAssignment.payRateTypeId
              ? currency(candidate.dealAssignment.payRate) + ' ' + getPayRateTypeLabel(candidate.dealAssignment.payRateTypeId)
              : 'Missing Pay Rate' }}
          </span>
        </div>

        <div class="item">
          <span class="label">Bill Rate</span>
          <span class="dots"></span>
          <span class="value">
            {{ !!candidate.dealAssignment.billRate && !!candidate.dealAssignment.billRateTypeId
              ? currency(candidate.dealAssignment.billRate) + ' ' + getBillRateTypeLabel(candidate.dealAssignment.billRateTypeId)
              : 'Missing Bill Rate' }}
          </span>
        </div>
      </div>
      <div v-else>
        <div
          v-for="commission in candidate.dealAssignment.commissions"
          :key="commission.id"
          class="item"
        >
          <span class="label">{{ commission.firstName }} {{ commission.lastName }}</span>
          <span class="dots"></span>
          <span class="value">
            {{ commission.commission | percent }}
          </span>
        </div>
        <div
          v-if="!candidate.dealAssignment.commissions || candidate.dealAssignment.commissions.length === 0"
          class="label mt-1"
        >
          No commissions for this contract
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SummaryTitle, SummaryItem } from '@/components/shared/display'
import { mapActions } from 'vuex'
import currency from '@/lib/display/formatCurrency'
import formatDate from '@/lib/display/formatDate'
import getPayRateTypeLabel from '@/lib/display/getPayRateTypeLabel'
import getBillRateTypeLabel from '@/lib/display/getBillRateTypeLabel'

export default {
  components: {
    SummaryTitle,
    SummaryItem
  },
  props: ['candidates', 'projectName'],
  data () {
    return {
      selectedView: 'contract',
      projectAnticipatedRevenue: null
    }
  },
  computed: {
    anticipatedRevenueInputs () {
      return JSON.stringify((this.candidates || []).map(c => ({
        billRateTypeId: c.dealAssignment.billRateTypeId,
        billRate: c.dealAssignment.billRate,
        workedHoursAverage: c.dealAssignment.workedHoursAverage,
        endAt: c.dealAssignment.endAt
      })))
    },
    candidatesWithoutEndDate () {
      return (this.candidates || []).filter(c => !c.dealAssignment.endAt)
    },
    candidatesWithPastEndDate () {
      return (this.candidates || []).filter(c => c.dealAssignment.endAt && new Date(c.dealAssignment.endAt) <= Date.now())
    },
    anticipatedRevenueHint () {
      const parts = []

      const missing = this.candidatesWithoutEndDate
      if (missing.length > 0) {
        const nameList = missing.map(c => `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown')
        const formattedNames = nameList.length > 1
          ? `${nameList.slice(0, -1).join(', ')} and ${nameList[nameList.length - 1]}`
          : nameList[0]
        const label = missing.length > 1 ? 'candidates' : 'candidate'
        const contractClause = missing.length > 1 ? "their contracts don't have an end date" : "their contract doesn't have an end date"
        parts.push(`Anticipated Revenue cannot be calculated for ${label}: ${formattedNames} since ${contractClause}`)
      }

      const past = this.candidatesWithPastEndDate
      if (past.length > 0) {
        const nameList = past.map(c => `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown')
        const formattedNames = nameList.length > 1
          ? `${nameList.slice(0, -1).join(', ')} and ${nameList[nameList.length - 1]}`
          : nameList[0]
        const label = past.length > 1 ? 'candidates' : 'candidate'
        const contractClause = past.length > 1 ? "their contracts' end date has gone past" : "their contract's end date has gone past"
        parts.push(`Anticipated Revenue cannot be calculated for ${label}: ${formattedNames} since ${contractClause}`)
      }

      return parts.join('\n\n')
    }
  },
  watch: {
    anticipatedRevenueInputs: {
      immediate: true,
      handler: async function () {
        const assignments = (this.candidates || []).map(c => c.dealAssignment)
        if (assignments.length > 0) {
          // Set the id, overtimeRate, overtimeThreshold, and workedHoursAverage to 0
          // since these are a required fields in the Assignments model in the backend.
          const assignmentsPayload = assignments.map(a => ({
            ...a,
            id: 0,
            overtimeRate: 0,
            overtimeThreshold: 0,
            workedHoursAverage: a.workedHoursAverage || 0
          }))

          this.projectAnticipatedRevenue = await this.getProjectAnticipatedRevenue(assignmentsPayload)
        } else {
          this.projectAnticipatedRevenue = null
        }
      }
    }
  },
  methods: {
    ...mapActions('projects', ['getProjectAnticipatedRevenue']),
    currency,
    formatDate,
    getPayRateTypeLabel,
    getBillRateTypeLabel
  }
}
</script>

<style scoped>
.card {
  max-width: 100% !important;
  padding: 16px;
  border: 1px solid #eee;
  border-radius: 12px;
  font-family: sans-serif;
}

h2 {
  margin-bottom: 16px;
  font-size: 18px;
}

/* Header with toggle */
.candidates-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

/* Toggle styles */
.toggle {
  display: flex;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

.toggle-btn {
  padding: 6px 12px;
  font-size: 13px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #666;
}

.toggle-btn:not(:last-child) {
  border-right: 1px solid #ddd;
}

.toggle-btn.active {
  background: #f5f5f5;
  color: #000;
  font-weight: 500;
}

/* Item rows with dot leaders */
.item {
  display: flex;
  align-items: center;
  padding: 6px 0;
}

.label {
  color: #777;
  font-weight: bold;
  font-size: 14px;
  white-space: nowrap;
}

.dots {
  flex: 1;
  border-bottom: 1px dotted #ccc;
  margin: 0 8px;
}

.value {
  font-weight: 500;
  white-space: nowrap;
}
</style>
